import { AfterViewInit, Component, effect, ElementRef, input, OnDestroy } from '@angular/core';
import { Store } from '@ngxs/store';
import { StripePlatformService } from '../../services';
import { SetProvidedBillingEmail } from '../../state';
import { BehaviorSubject, combineLatest, Observable, of, Subject, timer } from 'rxjs';
import {
  debounce,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  shareReplay,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { STRIPE_DEFAULT_PAYMENT_ELEMENT_OPTIONS, StripElementsInitializationData, StripePaymentElementCustomOptions } from '../../models';
import { PaymentMethodType } from '@aiwyn/api';
import { FieldOption, LayoutObject, PaymentWalletOption, PaymentWalletsOption, StripePaymentElementOptions } from '@stripe/stripe-js';
import { ignoreFalsy, ignoreNullish, Logger, TrackService } from '@aiwyn/shared';
import {
  isAWalletPaymentMethodType,
  PAYMENT_METHOD_CATEGORY_LOG_NAME,
  PAYMENT_METHOD_TYPE_TO_CATEGORY,
  PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED,
  shouldCollectEmailInElement,
} from '../../../models';
import { ConfigService } from '@aiwyn/config';
import { partition } from 'lodash';
import { AuthService } from '@aiwyn/auth';

@Component({
  selector: 'app-stripe-payment-element',
  templateUrl: './stripe-payment-element.component.html',
  styleUrls: ['./stripe-payment-element.component.scss'],
  standalone: false,
})
export class StripePaymentElementComponent implements AfterViewInit, OnDestroy {
  readonly userProvidedEmail = input<string | null>();

  private readonly _unsubscribe$ = new Subject<void>();

  private readonly _paymentElementReady$ = new BehaviorSubject<boolean>(false);

  private readonly _userEmail$ = this.authService.getUser(true).pipe(
    filter(user => !!user),
    map(user => user.getProviderEmail()),
    shareReplay(1),
    takeUntil(this._unsubscribe$),
  );

  initializeStripePaymentElement$ = combineLatest([
    this.stripePlatformService.getStripeElementInitializationData('payment').pipe(
      // new setup intent client secret signals stripe elements are to be created (or refreshed in the event of an invalid payment method)
      distinctUntilKeyChanged('setupIntentClientSecret'),
    ),
    this.stripePlatformService.getNewPaymentMethodTypeOptions().pipe(
      map(paymentMethodTypes => paymentMethodTypes.some(type => PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED.includes(type))),
      take(1), // could cause problems if we ever want to change accepted payment method types during a session
    ),
  ]).pipe(
    withLatestFrom(this._userEmail$),
    tap(([[initializationData, emailRequiredMethodEnabled], userEmail]) =>
      this.initializeStripePaymentElement(initializationData, emailRequiredMethodEnabled, userEmail)
    ),
    takeUntil(this._unsubscribe$),
  );

  private _getPaymentMethodTypeSelectionChanges$ = (shouldSkipFirstEmission: boolean = false): Observable<PaymentMethodType> =>
    this._paymentElementReady$.pipe(
      ignoreFalsy(),
      switchMap(_ => this.stripePlatformService.onPaymentElementChange$),
      ignoreNullish(),
      shouldSkipFirstEmission ? skip(1) : tap(),
      filter(({ collapsed }) => !collapsed),
      map(({ value }) => value.type as PaymentMethodType),
    );

  onPaymentMethodTypeSelection$ = this._getPaymentMethodTypeSelectionChanges$(true).pipe(
    distinctUntilChanged(),
    switchMap(paymentMethodType => this.stripePlatformService.setNewPaymentMethodType(paymentMethodType)),
    takeUntil(this._unsubscribe$),
  );

  logIncorrectInitialPaymentMethodTypeSelection$ = this.stripePlatformService.getNewPaymentMethodType().pipe(
    take(1),
    ignoreNullish(),
    switchMap(newPaymentMethodType => this._getPaymentMethodTypeSelectionChanges$().pipe(
      take(1),
      filter(selectedPaymentMethodType => selectedPaymentMethodType !== newPaymentMethodType),
      switchMap(selectedPaymentMethodType => this.stripePlatformService.getActiveSetupIntent().pipe(
        tap(activeSetupIntent => this.logger.error(
          'PAYMENT : StripePaymentElementComponent : logIncorrectInitialPaymentMethodTypeSelection$ :',
          `expected = ${newPaymentMethodType}, actual = ${selectedPaymentMethodType}.`,
          'activeSetupIntent =', activeSetupIntent,
        )),
      )),
    )),
    take(1),
    takeUntil(this._unsubscribe$),
  );

  onPaymentMethodSelectionTracking$ = this._getPaymentMethodTypeSelectionChanges$(false).pipe(
    tap(paymentMethodType => {
      const category = PAYMENT_METHOD_TYPE_TO_CATEGORY[paymentMethodType];
      const displayName = PAYMENT_METHOD_CATEGORY_LOG_NAME[category];

      this.trackService.track(`${displayName} Payment Method`, {
        type: paymentMethodType,
        action: 'selected'
      });
    }),
    takeUntil(this._unsubscribe$),
  );

  onWalletSelectionChange$ = this._getPaymentMethodTypeSelectionChanges$(false).pipe(
    map(paymentMethodType => isAWalletPaymentMethodType(paymentMethodType)),
    distinctUntilChanged(),
    tap(isWalletSelected => this.stripePlatformService.paymentElement$.value.update({
      fields: {
        billingDetails: {
          address: isWalletSelected ? 'auto' : 'never',
        },
      },
    })),
    takeUntil(this._unsubscribe$),
  );

  onEmailRequiredMethodSelectionChange$ = this._getPaymentMethodTypeSelectionChanges$(false).pipe(
    map(paymentMethodType => PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED.includes(paymentMethodType)),
    distinctUntilChanged(),
    withLatestFrom(this._userEmail$),
    debounce(([requiresBillingEmail]) => requiresBillingEmail ? of(undefined) : timer(350)), // wait until collapsed before hiding email field
    tap(([requiresBillingEmail, userEmail]) => {
      const collectEmailInElement = shouldCollectEmailInElement(requiresBillingEmail, this.getEmailForBilling(userEmail));
      this.stripePlatformService.paymentElement$.value.update({
        fields: { billingDetails: { email: collectEmailInElement ? 'auto' : 'never' } },
        defaultValues: { billingDetails: { email: undefined } },
      });
    }),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    private readonly host: ElementRef<StripePaymentElementComponent & HTMLElement>,
    private readonly stripePlatformService: StripePlatformService,
    private readonly configService: ConfigService,
    private readonly logger: Logger,
    private readonly authService: AuthService,
    private readonly trackService: TrackService,
    private readonly store: Store,
  ) {
    // Surface the buyer-supplied email to the platform service (which can't read the sidebar state
    // directly without an import cycle) so a hidden email field still satisfies the mandate.
    effect(() => this.store.dispatch(new SetProvidedBillingEmail(this.userProvidedEmail() || undefined)));
  }

  ngAfterViewInit(): void {
    this.onPaymentMethodTypeSelection$.subscribe();
    this.onPaymentMethodSelectionTracking$.subscribe();
    this.onWalletSelectionChange$.subscribe();
    this.onEmailRequiredMethodSelectionChange$.subscribe();
    this.initializeStripePaymentElement$.subscribe();
    this.logIncorrectInitialPaymentMethodTypeSelection$.subscribe();
  }

  // Prefer an email the buyer supplied outside the element (e.g. the sidebar); otherwise fall back
  // to the signed-in user's email, or undefined for a guest who supplied neither.
  private getEmailForBilling(userEmail?: string): string | undefined {
    const providedEmail = this.userProvidedEmail();
    if (providedEmail != null && providedEmail !== '') {
      return providedEmail;
    }
    // Get logged-in user's email as a fallback
    return userEmail;
  }

  initializeStripePaymentElement(
    { newPaymentMethodType }: StripElementsInitializationData,
    emailRequiredMethodEnabled: boolean,
    userEmail?: string,
  ): void {
    const email = this.getEmailForBilling(userEmail);
    const requiresBillingEmail = emailRequiredMethodEnabled && PAYMENT_METHODS_WITH_BILLING_EMAIL_REQUIRED.includes(newPaymentMethodType);
    const collectEmailInElement = shouldCollectEmailInElement(requiresBillingEmail, email);
    const paymentElementCustomOptions = this._getPaymentElementCustomOptions(newPaymentMethodType, collectEmailInElement);
    const paymentElementOptions = this._getPaymentElementOptions(paymentElementCustomOptions, email);

    const stripeElements = this.stripePlatformService.elements$.value;
    if (stripeElements.getElement('payment') != undefined) {
      // A setup-intent refresh can emit through this stream before the service swaps in a fresh elements group, in which
      // case `create` below throws IntegrationError ("Can only create one Element of type payment"), Stripe removes the
      // form entirely, and the thrown error kills this subscription (PP-3574). Destroy the previous element first.
      this.logger.warn(
        'PAYMENT : StripePaymentElementComponent : initializeStripePaymentElement : Destroying previously registered payment element.',
      );
      this.stripePlatformService.destroyPaymentElement();
    }

    const paymentElement = stripeElements.create('payment', paymentElementOptions);

    this.stripePlatformService.paymentElement$.next(paymentElement);
    this.stripePlatformService.paymentElement$.value.on('change', event => this.stripePlatformService.onPaymentElementChange$.next(event));
    this.stripePlatformService.paymentElement$.value.mount(this.host.nativeElement);

    this._paymentElementReady$.next(true);
  }

  private _getPaymentElementCustomOptions(
    newPaymentMethodType: PaymentMethodType,
    providedEmailRequired: boolean,
  ): StripePaymentElementCustomOptions {
    const defaultCollapsed: StripePaymentElementCustomOptions = newPaymentMethodType != undefined
      ? { defaultCollapsed: false }
      : {};

    const paymentMethodOrder: StripePaymentElementCustomOptions = {
      paymentMethodOrder: this._getPaymentMethodOrder(newPaymentMethodType)
    };

    const email: StripePaymentElementCustomOptions = { email: providedEmailRequired ? 'auto' : 'never' };
    const address = { address: this.getAddressFieldOption(newPaymentMethodType) };

    return { ...defaultCollapsed, ...paymentMethodOrder, ...email, ...address };
  }

  private _getPaymentMethodOrder(newPaymentMethodType?: PaymentMethodType): PaymentMethodType[] {
    // If no specific payment method type is provided, return the default order
    if (newPaymentMethodType === undefined) {
      // Separate wallets from other payment methods dynamically
      const [walletMethods, nonWalletMethods] = partition(Object.values(PaymentMethodType), isAWalletPaymentMethodType);

      // Return: wallets first, then other methods
      return [...walletMethods, ...nonWalletMethods];
    }

    // enforce Stripe payment method type consistency
    if (newPaymentMethodType === PaymentMethodType.ACSS) {
      newPaymentMethodType = PaymentMethodType.ACSS_DEBIT;
    } else if (newPaymentMethodType === PaymentMethodType.BACS) {
      newPaymentMethodType = PaymentMethodType.BACS_DEBIT;
    } else if (newPaymentMethodType === PaymentMethodType.ACH) {
      newPaymentMethodType = PaymentMethodType.US_BANK_ACCOUNT;
    }

    const allPaymentMethodOptions = [
      PaymentMethodType.CARD,
      PaymentMethodType.ACSS_DEBIT,
      PaymentMethodType.US_BANK_ACCOUNT,
      PaymentMethodType.BACS,
    ];

    const wallets = Object.values(PaymentMethodType).filter(type => isAWalletPaymentMethodType(type));
    return [newPaymentMethodType, ...wallets, ...allPaymentMethodOptions.filter(type => type !== newPaymentMethodType)];
  }

  private getAddressFieldOption(newPaymentMethodType: PaymentMethodType): FieldOption {
    return isAWalletPaymentMethodType(newPaymentMethodType) ? 'auto' : 'never';
  }

  private _getPaymentElementOptions(customOptions: StripePaymentElementCustomOptions = {}, email?: string): StripePaymentElementOptions {
    return {
      ...STRIPE_DEFAULT_PAYMENT_ELEMENT_OPTIONS,
      // don't provide undefined since Stripe "renders the experience that it determines will have the best conversion"
      ... {
        wallets: {
          googlePay: this.configService.googlePayEnabled ? 'auto' : 'never',
          applePay: this.configService.applePayEnabled ? 'auto' : 'never',
          link: this.configService.stripeLinkEnabled ? 'auto' : 'never',
        } as PaymentWalletsOption & { link?: PaymentWalletOption },
      },
      ...(
        customOptions.defaultCollapsed != null
          ? {
            layout: {
              ...STRIPE_DEFAULT_PAYMENT_ELEMENT_OPTIONS.layout as LayoutObject,
              defaultCollapsed: customOptions.defaultCollapsed,
            },
          }
          : {}
      ),
      fields: {
        billingDetails: {
          // this will only be set to 'auto' when the user is a guest
          email: customOptions.email ?? 'never',
          address: customOptions.address ?? 'never',
        },
      },
      defaultValues: { billingDetails: { email: email } },
      paymentMethodOrder: customOptions.paymentMethodOrder ?? undefined,
      readOnly: customOptions.readOnly ?? undefined,
    };
  }

  onClose(): Observable<unknown> {
    return of(undefined);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();

    // Don't let a buyer-supplied email linger in state for the next payment session.
    this.store.dispatch(new SetProvidedBillingEmail(undefined));

    this.stripePlatformService.destroyPaymentElement();
    /**
     * Refreshing the setup intent is the only way I could get the payment element to automatically
     * expand any type other than the session's initial selection.
     */
    this.stripePlatformService.getSetupIntentClientSecret(true);
  }
}
